import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { Bottone, Modale, Tabella } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * Credenziali dei programmi di posta (525, 547), per chi gestisce le utenze: chi usa la casella anche da
 * Thunderbird o da un altro programma, con IMAP e invio dal server di posta di CIDIS. Il nome utente e'
 * l'indirizzo della casella per tutte le persone; la password e' di una persona, e i messaggi spediti dal
 * programma risultano suoi.
 *
 * Qui l'elenco (persona, data, ultimo invio dal programma) e «Revoca». Le credenziali le crea chi gestisce
 * il sistema con ops/credenziale-posta.sh, che mostra la password una volta sola: il gestionale non la vede.
 * Senza `casellaId` (in «Caselle e ricezione») tutte le caselle; con `casellaId` (nella configurazione di
 * una casella) solo quella, e senza credenziali il riquadro non compare.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
const quando = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

export function CredenzialiProgramma({ casellaId }: { casellaId?: string }) {
  const [elenco, setElenco] = useState<posta.CredenzialeProgramma[] | null>(null)
  const [daRevocare, setDaRevocare] = useState<posta.CredenzialeProgramma | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)

  const carica = useCallback(() => {
    posta.elencaCredenziali(casellaId).then(setElenco).catch((e) => setErrore(testoErrore(e)))
  }, [casellaId])
  useEffect(() => { carica() }, [carica])

  async function revoca() {
    if (!daRevocare) return
    setInCorso(true); setErrore(null)
    try {
      await posta.revocaCredenziale(daRevocare.id)
      setMessaggio(`Credenziale di ${daRevocare.persona.nome} ${daRevocare.persona.cognome} revocata: il suo `
        + 'programma non entra più entro qualche minuto. Le altre persone della casella non cambiano.')
      setDaRevocare(null)
      carica()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  if (casellaId && elenco && elenco.length === 0 && !messaggio) return null

  return (
    <div>
      <div className="posta-titoletto">Programmi di posta</div>
      <p className="piccolo tenue">
        Persone che usano {casellaId ? 'la casella' : 'una casella'} anche da Thunderbird o da un altro programma:
        nome utente uguale all’indirizzo della casella, una password per persona. Ciò che il programma spedisce
        risulta di chi ha la password. «Ultimo invio»: l’ultimo messaggio spedito dal programma. Una credenziale
        nuova la crea chi gestisce il sistema; se la persona esce dalla casella, passa a «legge» o la sua utenza
        si disattiva, la credenziale si revoca da sola.
      </p>
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      {errore && !daRevocare && <Avviso tipo="errore">{errore}</Avviso>}
      {!elenco && !errore && <Attesa testo="Caricamento…" />}
      {elenco && (
        <Tabella intestazioni={[...(casellaId ? [] : ['Casella']), 'Persona', 'Creata', 'Ultimo invio',
                                <span className="solo-lettore" key="azioni">Azioni</span>]}
                 vuoto={<Vuoto testo="Nessuna credenziale di un programma di posta." />}>
          {elenco.map((c) => (
            <tr key={c.id}>
              {!casellaId && (
                <td data-etichetta="Casella">
                  <div className="voce-principale">{c.casella.nome_visualizzato}</div>
                  <div className="piccolo tenue">{c.casella.indirizzo}</div>
                </td>
              )}
              <td data-etichetta="Persona">
                <div>{c.persona.cognome} {c.persona.nome}</div>
                <div className="piccolo tenue">{c.persona.email}</div>
              </td>
              <td data-etichetta="Creata" className="piccolo">
                {giorno.format(new Date(c.creata_il))}
                {c.creata_da && <div className="tenue">da {c.creata_da.nome} {c.creata_da.cognome}</div>}
              </td>
              <td data-etichetta="Ultimo invio" className="piccolo">
                {c.ultimo_uso_il ? quando.format(new Date(c.ultimo_uso_il)) : <span className="tenue">nessuno</span>}
              </td>
              <td data-etichetta="Azioni">
                <Bottone dimensione="piccolo" variante="discreto"
                         aria-label={`Revoca la credenziale di ${c.persona.nome} ${c.persona.cognome} su ${c.casella.indirizzo}`}
                         onClick={() => { setErrore(null); setMessaggio(null); setDaRevocare(c) }}>
                  Revoca
                </Bottone>
              </td>
            </tr>
          ))}
        </Tabella>
      )}
      {/* Nel corpo della pagina: dentro la modifica della casella una finestra annidata resterebbe nascosta. */}
      {daRevocare && createPortal(
        <Modale titolo="Revoca la credenziale" chiudi={() => setDaRevocare(null)} fuocoIniziale="azioni"
          azioni={
            <>
              <Bottone variante="discreto" onClick={() => setDaRevocare(null)}>Annulla</Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void revoca()}>Revoca</Bottone>
            </>
          }>
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          <p>
            {daRevocare.persona.nome} {daRevocare.persona.cognome} non entrerà più in {daRevocare.casella.indirizzo} dal
            suo programma di posta: entro qualche minuto il server rifiuta la sua password e chiude le connessioni
            aperte. Le altre persone della casella continuano con le loro password; nel gestionale non cambia niente.
          </p>
          <p className="piccolo tenue">
            La posta già scaricata resta sul suo computer. Per riaprire l’accesso serve una credenziale nuova. La
            revoca resta nel registro eventi.
          </p>
        </Modale>,
        document.body,
      )}
    </div>
  )
}
